import React from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { Col, Row } from "components/Containers";
import { colors, fontSize } from "style";
import { formatCurrency, formatNumber } from "utils";
import { ApplicantScore } from "components/Missions/Applicants";
import Rate from "components/Rate";

/**
 * ApplicantRate
 *
 * @param {Object}  data
 * @param {Boolean} showBid
 * @param {Boolean} showEstimate
 */
const ApplicantRate = ({ data, showBid = true, showEstimate = false, ...props }) => {
  const bidRate = formatCurrency(data?.hourlyRateData?.applicantHourlyRateUsd);
  const applicantHourlyRateUsdDelta = formatNumber(data?.hourlyRateData?.applicantHourlyRateUsdDelta, "", 2);
  const estRate = formatCurrency(data?.hourlyRateData?.consideredHourlyRateUsd);
  const consideredHourlyRateUsdDelta = formatNumber(data?.hourlyRateData?.consideredHourlyRateUsdDelta, "", 2);
  const buyRate = formatCurrency(data?.negotiatedRate?.amount, data?.negotiatedRate?.currency);
  const sellRate = formatCurrency(data?.negotiatedSellingRate?.amount, data?.negotiatedSellingRate?.currency);
  const hasEstRate = parseFloat(data?.hourlyRateData?.consideredHourlyRateUsd) > 0;
  const hasBidRate = parseFloat(data?.hourlyRateData?.applicantHourlyRateUsd) > 0;
  const hasBuyRate = parseFloat(data?.negotiatedRate?.amount) > 0;
  const hasSellRate = parseFloat(data?.negotiatedSellingRate?.amount) > 0;

  return (
    <Col css={styles.container} gap="0.35rem" {...props}>
      <ApplicantScore {...data} />
      {showBid && (
        <RatePillsWithDelta
          hasRate={hasBidRate}
          rate={bidRate}
          deltaRate={applicantHourlyRateUsdDelta}
          labelId="Missions.Applicants.ApplicantRate.LabelBid"
        />
      )}
      {showEstimate && (
        <RatePillsWithDelta
          hasRate={hasEstRate}
          rate={estRate}
          deltaRate={consideredHourlyRateUsdDelta}
          labelId="Missions.Applicants.ApplicantRate.LabelEstimate"
        />
      )}
      {hasBuyRate && (
        <Rate labelId="Missions.Applicants.ApplicantRate.LabelBuy" messageId="Global.PerHour" rate={buyRate} />
      )}
      {hasSellRate && (
        <Rate labelId="Missions.Applicants.ApplicantRate.LabelSell" messageId="Global.PerHour" rate={sellRate} />
      )}
    </Col>
  );
};

const RatePillsWithDelta = ({ hasRate, rate, deltaRate, labelId }) => (
  <Row gap="0.35rem">
    {hasRate ? (
      <Rate labelId={labelId} messageId="Global.PerHour" rate={rate} />
    ) : (
      <Rate labelId={labelId} messageId="Global.EmDash" rate="" />
    )}
    {deltaRate != "—" && <div css={styles.delta_rate(deltaRate > 0)}>{`${deltaRate > 0 ? `+` : ``}${deltaRate}`}</div>}
  </Row>
);

RatePillsWithDelta.propTypes = {
  hasRate: PropTypes.bool,
  rate: PropTypes.string,
  deltaRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  labelId: PropTypes.string,
};

const styles = {
  container: css`
    align-items: center;
    white-space: nowrap;
    align-items: flex-start;
  `,
  delta_rate: (isPositive) => css`
    background: ${isPositive ? colors.lightRed : colors.lightGreen};
    color: ${isPositive ? colors.red : colors.green};
    padding: 0.25rem 0.5rem;
    border-radius: 1.6rem;
    font-size: ${fontSize.xxsmall};
    font-weight: 600;
  `,
};

ApplicantRate.propTypes = {
  data: PropTypes.object,
  showBid: PropTypes.bool,
  showEstimate: PropTypes.bool,
};

export default ApplicantRate;
